<template>
  <div class="po-index-page po main-content">
    <div class="page-header">
      <h3 class="page-title">{{ $t("pos.title") }}</h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $tc("pos.po", 2) }}
          </router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div v-if="userType == 'anchor'" class="col-xs-2 mr-4">
          <base-button
            color="theme"
            icon="file"
            size="large"
            @click="ImportItemFile"
          >
            {{ $t("general.import_file") }}
          </base-button>
        </div>
        <div class="col-xs-2 mr-4">
          <base-button
            v-show="totalPOs || filtersApplied"
            :outline="true"
            :icon="filterIcon"
            size="large"
            color="theme"
            right-icon
            @click="toggleFilter"
          >
            {{ $t("general.filter") }}
          </base-button>
        </div>
        <router-link
          v-if="userType == 'anchor'"
          slot="item-title"
          class="col-xs-2"
          to="purchase-orders/create"
        >
          <base-button size="large" icon="plus" color="theme">
            {{ $t("pos.new_po") }}</base-button
          >
        </router-link>
      </div>
    </div>

    <transition name="fade">
      <div v-show="showFilters" class="filter-section">
        <div class="filter-container">
          <div v-if="userType == 'supplier'" class="filter-customer">
            <label>{{ $tc("customers.customer", 1) }} </label>
            <base-select
              v-model="filters.customer"
              :options="customers"
              :searchable="true"
              :show-labels="false"
              :allow-empty="true"
              :tabindex="8"
              :placeholder="$t('customers.type_or_click')"
              label="name"
              track-by="id"
            />
          </div>

          <div v-if="userType == 'anchor'" class="filter-customer">
            <label class="form-label">{{ $t("invoices.supplier", 1) }}</label>
            <base-select
              v-model="filters.supplier"
              :options="suppliers"
              :searchable="true"
              :show-labels="false"
              :allow-empty="true"
              :tabindex="8"
              :placeholder="$t('customers.type_or_click')"
              label="company_name"
              track-by="id"
            />
          </div>

          <div class="filter-status">
            <label>{{ $t("pos.status") }}</label>
            <base-select
              v-if="userType == 'anchor'"
              v-model="filters.status"
              :options="anchorPOstatuses"
              :group-select="false"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('general.select_a_status')"
              group-values="options"
              group-label="label"
              track-by="name"
              label="name"
              @remove="clearStatusSearch()"
            />
            <base-select
              v-else-if="userType == 'supplier'"
              v-model="filters.status"
              :options="supplierPOstatuses"
              :group-select="false"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('general.select_a_status')"
              group-values="options"
              group-label="label"
              track-by="name"
              label="name"
              @remove="clearStatusSearch()"
            />

            <base-select
              v-else-if="userType == 'super_admin'"
              v-model="filters.status"
              :options="superAdminPOstatuses"
              :group-select="false"
              :searchable="true"
              :show-labels="false"
              :placeholder="$t('general.select_a_status')"
              group-values="options"
              group-label="label"
              track-by="name"
              label="name"
              @remove="clearStatusSearch()"
            />
          </div>
          <div class="filter-date">
            <div class="from pr-3">
              <label>{{ $t("general.from") }}</label>
              <base-date-picker
                v-model="filters.from_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
              />
            </div>
            <div class="dashed" />
            <div class="to pl-3">
              <label>{{ $t("general.to") }}</label>
              <base-date-picker
                v-model="filters.to_date"
                :calendar-button="true"
                calendar-button-icon="calendar"
              />
            </div>
          </div>
          <div class="filter-po">
            <label>{{ $t("pos.po_number") }}</label>
            <base-input v-model="filters.po_number" icon="hashtag" />
          </div>
        </div>
        <label class="clear-filter" @click="clearFilter">{{
          $t("general.clear_all")
        }}</label>
      </div>
    </transition>
    <div
      v-cloak
      v-show="showEmptyScreen"
      class="col-xs-1 no-data-info"
      align="center"
    >
      <moon-walker-icon class="mt-5 mb-4" />
      <div class="row" align="center">
        <label class="col title">{{ $t("pos.no_po") }}</label>
      </div>
      <div class="row">
        <label class="description col mt-1" align="center">{{
          $t("pos.list_of_po")
        }}</label>
      </div>
      <div v-if="userType == 'anchor'" class="btn-container">
        <base-button
          :outline="true"
          color="theme"
          class="mt-3"
          size="large"
          @click="$router.push('purchase-orders/create')"
        >
          {{ $t("pos.add_new_po") }}
        </base-button>
      </div>
    </div>

    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ POs.length }}</b>
          {{ $t("general.of") }} <b>{{ totalPOs }}</b>
        </p>

        <!-- Tabs -->
        <ul class="tabs">
          <li class="tab" @click="getStatus('', '')">
            <a
              :class="[
                'tab-link',
                {
                  'a-active':
                    filters.status.value === '' ||
                    (filters.status.value !== 'DRAFT' &&
                      filters.status.value !== 'SENT' &&
                      filters.status.value !== 'ACCEPTED' &&
                      filters.status.value !== 'REJECTED' &&
                      filters.status.value !== 'SETTLED')
                }
              ]"
              href="#"
              >{{ $t("general.all") }}</a
            >
          </li>
          <li
            v-if="userType == 'anchor'"
            class="tab"
            @click="getStatus('DRAFT', 'DRAFT')"
          >
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'DRAFT' }
              ]"
              href="#"
              >{{ $t("general.draft") }}</a
            >
          </li>
          <li
            v-if="userType == 'funder' || userType == 'super_admin'"
            class="tab"
            @click="getStatus('VERIFIED', 'VERIFIED')"
          >
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'VERIFIED' }
              ]"
              href="#"
              >{{ $t("general.verified") }}</a
            >
          </li>
          <li class="tab" @click="getStatus('SENT', 'SENT')">
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'SENT' }
              ]"
              href="#"
            >
              <span v-if="userType == 'supplier'">{{
                $t("general.received")
              }}</span>
              <span v-else>{{ $t("general.sent") }}</span>
            </a>
          </li>
          <li class="tab" @click="getStatus('ACCEPTED', 'ACCEPTED')">
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'ACCEPTED' }
              ]"
              href="#"
              >{{ $t("general.accepted") }}</a
            >
          </li>
          <li class="tab" @click="getStatus('REJECTED', 'REJECTED')">
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'REJECTED' }
              ]"
              href="#"
              >{{ $t("general.rejected") }}</a
            >
          </li>
          <li
            class="tab"
            @click="
              getStatus('FINANCED', [
                'REQUESTED',
                'OFFERED',
                'PREPAID',
                'FINANCE_APPROVED',
                'VERIFIED',
                'PENDING'
              ])
            "
          >
            <a
              :class="['tab-link', { 'a-active': isFinancedTabActive }]"
              href="#"
              >{{ $t("general.financed") }}</a
            >
          </li>
          <li class="tab" @click="getStatus('SETTLED', 'SETTLED')">
            <a
              :class="[
                'tab-link',
                { 'a-active': filters.status.value === 'SETTLED' }
              ]"
              href="#"
              >{{ $t("general.settled") }}</a
            >
          </li>
        </ul>
        <transition name="fade">
          <v-dropdown v-if="selectedPOs.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
            <v-dropdown-item>
              <div
                v-if="userType == 'anchor'"
                class="dropdown-item"
                @click="removeMultiplepo"
              >
                <font-awesome-icon
                  :icon="['fas', 'trash']"
                  class="dropdown-item-icon"
                />
                {{ $t("general.delete") }}
              </div>
            </v-dropdown-item>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          :disabled="POs.length <= 0"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllPOs"
        />
        <label
          v-show="!isRequestOngoing"
          for="select-all"
          class="custom-control-label selectall"
        >
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :show-filter="false"
        :data="fetchData"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              />
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column
          :label="$t('pos.date')"
          sort-as="purchase_order_date"
          show="formattedPurchaseOrderDate"
        />
        <table-column
          :label="$t('pos.due_date')"
          sort-as="expiry_date"
          show="formattedExpiryDate"
        />
        <table-column
          v-if="userType == 'anchor' || userType == 'super_admin'"
          :label="$t('pos.supplier')"
        >
          <template slot-scope="row">
            <p v-if="row.supplier_stakeholder">
              <span
                v-if="
                  row.supplier_stakeholder.details.company &&
                    row.supplier_stakeholder.details.company.name
                "
              >
                {{ row.supplier_stakeholder.details.company.name }}
              </span>
              <!-- {{ row.supplier_stakeholder.details.user.name }} -->
            </p>
          </template>
        </table-column>
        <table-column
          v-if="userType == 'supplier' || userType == 'super_admin'"
          :label="$t('pos.customer')"
        >
          <template slot-scope="row">
            <p v-if="row.buyer_stakeholder">
              <span v-if="row.buyer_stakeholder.details.company.name">
                {{ row.buyer_stakeholder.details.company.name }}
              </span>
              <!-- {{ row.buyer_stakeholder.details.user.name }} -->
            </p>
          </template>
        </table-column>
        <table-column :label="$t('pos.status')" show="status">
          <template slot-scope="row">
            <span> {{ $t("pos.status") }}</span>
            <!-- <span :class="'po-status-' + row.status.toLowerCase()">{{
              row.status
            }}</span> -->
            <span
              v-if="userType == 'supplier' && row.status == 'SENT'"
              :class="'inv-status-' + row.status.toLowerCase()"
              >{{ $t("general.received") }}</span
            >
            <span
              v-else-if="userType == 'funder' && row.status == 'PENDING'"
              :class="'inv-status-' + row.status.toLowerCase()"
            >
              {{ row.status }} - {{ row.funderinvoices.user.name }}
            </span>
            <span v-else :class="'inv-status-' + row.status.toLowerCase()">{{
              row.status.toUpperCase()
            }}</span>
          </template>
          <!-- <template slot-scope="row">
            <span> {{ $t("pos.status") }}</span>
            <span
              v-if="userType == 'supplier' && row.status == 'SENT'"
              :class="'po-status-' + row.status.toLowerCase()"
              >{{ $t("general.received") }}</span
            >
            <span
              v-else-if="userType == 'anchor' || userType == 'super_admin'"
              :class="'po-status-' + row.status.toLowerCase()"
              >{{ row.status }}</span
            >
            <span
              v-else-if="row.status != 'SENT'"
              :class="'po-status-' + row.status.toLowerCase()"
              >{{
                row.status != "PARTIALLY_SETTLED"
                  ? row.status
                  : row.status.replace("_", " ")
              }}</span
            >
          </template> -->
        </table-column>
        <!-- <table-column
          :label="'delivery_note_status'"
          show="delivery_note_status"/> -->
        <table-column :label="$tc('pos.po_num', 1)" show="reference_number" />
        <table-column :label="$t('invoices.total')" sort-as="total">
          <template slot-scope="row">
            <span> {{ $t("pos.total") }}</span>
            <div
              v-html="
                $utils.formatMoney(row.total, row.supplier.account.currency)
              "
            />
          </template>
        </table-column>
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span> {{ $t("pos.action") }} </span>
            <v-dropdown>
              <a slot="activator" href="#">
                <dot-icon />
              </a>
              <v-dropdown-item>
                <router-link
                  v-if="row.status == 'DRAFT' && userType == 'anchor'"
                  :to="{ path: `purchase-orders/${row.id}/edit` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'pencil-alt']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.edit") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item>
                <div
                  v-if="row.status == 'DRAFT' && userType == 'anchor'"
                  class="dropdown-item"
                  @click="removepo(row.id)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'trash']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.delete") }}
                </div>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="userType == 'anchor' || userType == 'supplier'"
              >
                <router-link
                  :to="{ path: `purchase-orders/${row.id}/view` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                  {{ $t("general.view") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item v-else-if="userType == 'funder'">
                <router-link
                  :to="{ path: `purchaseorder-financing/${row.id}/view` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                  {{ $t("general.view") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="userType === 'anchor' || userType === 'supplier'"
              >
                <router-link
                  :to="{ path: `purchase-orders/${row.id}/attachment` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon icon="file" class="dropdown-item-icon" />
                  {{ $t("general.attachments") }}
                </router-link>
              </v-dropdown-item>

              <v-dropdown-item>
                <a
                  v-if="
                    row.status == 'OPEN' ||
                      row.status == 'ACCEPTED' ||
                      row.status == 'REJECTED' ||
                      row.status == 'SETTLED' ||
                      row.status == 'PARTIALLY_SETTLED' ||
                      row.status == 'MANUALLY_ADD_ITEMS'
                  "
                  href="#/"
                  class="dropdown-item"
                  @click.self="completionStatus(row.id)"
                >
                  <font-awesome-icon icon="star" class="dropdown-item-icon" />
                  {{ $t("pos.completion_status") }}
                </a>
              </v-dropdown-item>

              <v-dropdown-item
                v-if="
                  row.item_discrepancy.length > 0 &&
                    (userType == 'anchor' || userType == 'supplier')
                "
              >
                <router-link
                  :to="{ path: `purchase-orders/${row.id}/discrepency_report` }"
                  class="dropdown-item"
                  href="#/"
                >
                  <font-awesome-icon icon="random" class="dropdown-item-icon" />
                  {{ $t("pos.discrepancy_report") }}
                </router-link>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="row.status == 'DRAFT' && userType == 'anchor'"
              >
                <a class="dropdown-item" href="#/" @click.self="sendpo(row.id)">
                  <font-awesome-icon
                    icon="paper-plane"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.send_po") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  row.status != 'DRAFT' &&
                    row.status != 'SETTLED' &&
                    userType == 'anchor'
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click.self="closePO(row.id)"
                >
                  <font-awesome-icon icon="times" class="dropdown-item-icon" />
                  {{ $t("pos.close_po") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  (row.status == 'SENT' || row.status == 'VIEWED') &&
                    userType == 'supplier'
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click.self="onMarkAsAccepted(row.id)"
                >
                  <font-awesome-icon
                    icon="check-circle"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.mark_as_accepted") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  (row.status == 'SENT' || row.status == 'VIEWED') &&
                    userType == 'supplier'
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click.self="onMarkAsRejected(row.id)"
                >
                  <font-awesome-icon
                    icon="times-circle"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.mark_as_rejected") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  (row.status == 'ACCEPTED' || row.status == 'OPEN') &&
                    (row.item_status == 'ACCEPTED' ||
                      row.item_status == 'PARTIALLY_SETTLED') &&
                    userType == 'supplier'
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click="convertInToDeliveryNote(row.id)"
                >
                  <font-awesome-icon
                    icon="shipping-fast"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.convert_to_delivery_note") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="
                  (userType == 'supplier' || userType == 'funder') &&
                    (row.status == 'OFFERED' || row.status == 'FINANCED')
                "
              >
                <a
                  href="#/"
                  class="dropdown-item"
                  @click="viewOffer(row.id, row.status)"
                >
                  <font-awesome-icon
                    :icon="['far', 'comment']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.view_offer") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="userType == 'funder' && row.status == 'FINANCED'"
              >
                <a class="dropdown-item" @click="makePrepayment(row.id)">
                  <font-awesome-icon
                    :icon="['fas', 'check']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.make_prepayment") }}
                </a>
              </v-dropdown-item>
              <v-dropdown-item
                v-if="row.status == 'ACCEPTED' && userType == 'supplier'"
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click="sendPOForFinancing(row.id)"
                >
                  <font-awesome-icon
                    icon="file-invoice"
                    class="dropdown-item-icon"
                  />
                  {{ $t("pos.apply_for_po_financing") }}
                </a>
              </v-dropdown-item>

              <v-dropdown-item>
                <!-- v-if="
                row.status == 'REQUESTED' ||
                row.status == 'VERIFIED' ||
                row.status == 'SETTLED' ||
                row.status == 'PARTIALLY_SETTLED' ||
                row.status == 'MANUALLY_ADD_ITEMS'
                " -->
                <a
                  v-if="userType == 'super_admin'"
                  class="dropdown-item"
                  @click="reviewPrepaymentRequest(row)"
                >
                  <font-awesome-icon
                    :icon="['far', 'comment']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.review_prePayment_request") }}
                </a>
              </v-dropdown-item>

              <v-dropdown-item
                v-if="userType == 'funder' && row.status == 'PENDING'"
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click="openPrepaymentPending(row.id)"
                >
                  <font-awesome-icon
                    :icon="['far', 'comment']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.review_prePayment_request") }}
                </a>
              </v-dropdown-item>

              <v-dropdown-item
                v-if="userType == 'super_admin' && row.status == 'VERIFIED'"
              >
                <router-link
                  :to="`/super-admin/purchase-orders/${row.id}/funder`"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    icon="paper-plane"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.sendToFunder") }}
                </router-link>
              </v-dropdown-item>

              <v-dropdown-item  v-if="userType == 'anchor' || userType == 'supplier'">
                <router-link
                  :to="{ path: `purchase-orders/${row.id}/record` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'history']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.action_record") }}
                </router-link>
              </v-dropdown-item>

              <v-dropdown-item v-else-if="userType == 'funder'" >
                <router-link
                  :to="{ path: `purchaseorder-financing/${row.id}/record` }"
                  class="dropdown-item"
                >
                  <font-awesome-icon
                    :icon="['fas', 'history']"
                    class="dropdown-item-icon"
                  />
                  {{ $t("general.action_record") }}
                </router-link>
              </v-dropdown-item>

              <v-dropdown-item
                v-if="
                  row.status != 'REJCECTED' &&
                    row.status != 'SENT' &&
                    row.status != 'DRAFT' &&
                    row.status != 'VIEWED' &&
                    userType == 'supplier' &&
                    row.delivery_notes.length > 0
                "
              >
                <a
                  class="dropdown-item"
                  href="#/"
                  @click="PODeliveryNotes(row.id)"
                >
                  <font-awesome-icon
                    icon="shipping-fast"
                    class="dropdown-item-icon"
                  />
                  {{ $t("deliveryNotes.title") }}
                </a>
              </v-dropdown-item>

              <v-dropdown-item
                v-if="
                  row.status != 'REJCECTED' &&
                    row.status != 'SENT' &&
                    row.status != 'DRAFT' &&
                    row.status != 'VIEWED' &&
                    (userType == 'supplier' || userType == 'anchor') &&
                    row.invoices.length > 0
                "
              >
                <a class="dropdown-item" href="#/" @click="POInvoices(row.id)">
                  <font-awesome-icon
                    icon="file-alt"
                    class="dropdown-item-icon"
                  />
                  {{ $t("invoices.title") }}
                </a>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import MoonWalkerIcon from "../../../js/components/icon/MoonwalkerIcon";
import moment from "moment";

export default {
  components: {
    "moon-walker-icon": MoonWalkerIcon
  },
  data() {
    return {
      showFilters: false,
      currency: null,
      superAdminPOstatuses: [
        {
          label: "Status",
          isDisable: true,
          options: [
            { name: "SENT", value: "SENT" },
            { name: "VIEWED", value: "VIEWED" },
            { name: "ACCEPTED", value: "ACCEPTED" },
            { name: "REJECTED", value: "REJECTED" },
            { name: "PARTIALLY SETTLED", value: "PARTIALLY_SETTLED" },
            { name: "SETTLED", value: "SETTLED" }
          ]
        }
      ],
      anchorPOstatuses: [
        {
          label: "Status",
          isDisable: true,
          options: [
            { name: "DRAFT", value: "DRAFT" },
            { name: "SENT", value: "SENT" },
            { name: "VIEWED", value: "VIEWED" },
            { name: "ACCEPTED", value: "ACCEPTED" },
            { name: "REJECTED", value: "REJECTED" },
            { name: "PARTIALLY SETTLED", value: "PARTIALLY_SETTLED" },
            { name: "SETTLED", value: "SETTLED" }
          ]
        }
      ],
      supplierPOstatuses: [
        {
          label: "Status",
          isDisable: true,
          options: [
            { name: "RECEIVED", value: "SENT" },
            { name: "VIEWED", value: "VIEWED" },
            { name: "ACCEPTED", value: "ACCEPTED" },
            { name: "REJECTED", value: "REJECTED" },
            { name: "INVOICE GENERATE", value: "INVOICE_GENERATE" },
            { name: "PARTIALLY SETTLED", value: "PARTIALLY_SETTLED" },
            { name: "SETTLED", value: "SETTLED" }
          ]
        }
      ],
      filtersApplied: false,
      isRequestOngoing: true,
      filters: {
        supplier: "",
        customer: "",
        status: {
          name: "",
          value: ""
        },
        from_date: "",
        to_date: "",
        po_number: ""
      },
      userType: null
    };
  },

  computed: {
    focus,
    showEmptyScreen() {
      return !this.totalPOs && !this.isRequestOngoing && !this.filtersApplied;
    },
    filterIcon() {
      return this.showFilters ? "times" : "filter";
    },
    ...mapGetters("customer", ["customers"]),
    ...mapGetters("supplier", ["suppliers"]),
    ...mapGetters("currency", ["defaultCurrency", "currencies"]),
    ...mapGetters("po", ["selectedPOs", "totalPOs", "POs", "selectAllField"]),
    selectField: {
      get: function() {
        return this.selectedPOs;
      },
      set: function(val) {
        this.selectPO(val);
      }
    },
    isFinancedTabActive() {
      const isActive = this.filters.status.name === "FINANCED";
      return isActive;
    },
    selectAllFieldStatus: {
      get: function() {
        return this.selectAllField;
      },
      set: function(val) {
        this.setSelectAllState(val);
      }
    }
  },
  watch: {
    filters: {
      handler: "setFilters",
      deep: true
    }
  },
  created() {
    if (localStorage.getItem("role") === "super_admin") {
      this.userType = "super_admin";
    } else if (localStorage.getItem("account_type") === "anchor") {
      this.userType = "anchor";
    } else if (localStorage.getItem("account_type") === "funder") {
      this.userType = "funder";
    } else if (localStorage.getItem("account_type") === "supplier") {
      this.userType = "supplier";
    }
    if (this.userType === "supplier") {
      let data = {
        parent_account_exist: true,
        pagination: "no"
      };
      this.fetchCustomers(data);
    }
    if (this.userType === "anchor") {
      this.fetchSuppliers({ pagination: "no" });
    }
  },
  destroyed() {
    if (this.selectAllField) {
      this.selectAllPOs();
    }
  },
  methods: {
    ...mapActions("supplier", ["fetchSuppliers"]),

    ...mapActions("po", [
      "fetchPOs",
      "resetSelectedPOs",
      "getRecord",
      "selectPO",
      "selectAllPOs",
      "deletePO",
      "deleteMultiplePOs",
      "markAsSent",
      "markAsClose",
      "convertToInvoice",
      "convertToDeliveryNote",
      "setSelectAllState",
      "markAsAccepted",
      "markAsRejected",
      "sendEmail",
      "setPOForDeliveryNotes",
      "setPOForInvoices"
    ]),
    ...mapActions("customer", ["fetchCustomers"]),
    ...mapActions("modal", ["openModal"]),
    refreshTable() {
      this.$refs.table.refresh();
    },
    getStatus(name, val) {
      this.filters.status = {
        name: name,
        value: val
      };
    },
    async fetchData({ page, per_page, filter, sort }) {
      let Data = {
        supplier_id:
          this.filters.supplier === ""
            ? this.filters.supplier
            : this.filters.supplier.id,
        user_id:
          this.filters.customer === ""
            ? this.filters.customer
            : this.filters.customer.parent_account_id,
        status: this.filters.status.value,
        from_date:
          this.filters.from_date === ""
            ? this.filters.from_date
            : moment(this.filters.from_date).format("DD/MM/YYYY"),
        to_date:
          this.filters.to_date === ""
            ? this.filters.to_date
            : moment(this.filters.to_date).format("DD/MM/YYYY"),
        po_number: this.filters.po_number,
        orderByField: sort.fieldName || "created_at",
        orderBy: sort.order || "desc",
        per_page, // Add rowsPerPage in the API request
        page
      };
      this.isRequestOngoing = true;
      let response = await this.fetchPOs(Data);

      this.isRequestOngoing = false;

      this.currency = response.data.currency;

      return {
        data: response.data.purchaseOrders.data,
        pagination: {
          totalPages: response.data.purchaseOrders.last_page,
          currentPage: page,
          count: response.data.purchaseOrders.count,
          rowsPerPage: response.data.purchaseOrders.per_page // Add rowsPerPage in the API request
        }
      };
    },
    async viewOffer(id, status) {
      if (this.userType === "funder") {
        this.$router.push(`/funder/purchaseorder-financing/${id}/offer`);
      } else {
        this.$router.push(`/supplier/purchase-orders/${id}/offer`);
      }
    },

    async makePrepayment(id) {
      this.$router.push(`/funder/payments/${id}/po-prepayment/create`);
    },
    async onMarkAsAccepted(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_mark_as_accepted"),
        icon: "/assets/icon/check-circle-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async markedAsRejected => {
        if (markedAsRejected) {
          const data = {
            purchase_order_id: id
          };
          let response = await this.markAsAccepted(data);
          this.refreshTable();
          if (response.data) {
            this.filters.status = { name: "", value: "" };
            this.$refs.table.refresh();
            window.toastr["success"](
              this.$tc("pos.marked_as_accepted_message")
            );
          }
        }
      });
    },
    async PODeliveryNotes(id) {
      await this.setPOForDeliveryNotes(id);
      this.$router.push("delivery-notes");
    },
    POInvoices(id) {
      this.setPOForInvoices(id);
      this.$router.push("invoices");
    },
    async onMarkAsRejected(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_mark_as_rejected"),
        icon: "/assets/icon/times-circle-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async markedAsRejected => {
        if (markedAsRejected) {
          const data = {
            purchase_order_id: id
          };
          let response = await this.markAsRejected(data);
          this.refreshTable();
          if (response.data) {
            this.filters.status = { name: "", value: "" };
            this.$refs.table.refresh();
            window.toastr["success"](
              this.$tc("pos.marked_as_rejected_message")
            );
          }
        }
      });
    },
    async openPrepaymentVerified(id) {
      this.$router.push(
        `/super-admin/purchase-orders/prepayments/${id}/verified`
      );
    },
    async reviewPrepaymentRequest(row) {
      if (row.status === "REQUESTED") {
        this.openPrepaymentVerified(row.id);
      } else {
        this.$router.push(
          `/super-admin/purchase-orders/prepayments/${row.id}/view`
        );
      }
    },
    async openPrepaymentPending(id) {
      this.$router.push(`/funder/purchaseorder-financing/${id}/pending`);
    },
    setFilters() {
      this.filtersApplied = true;
      this.resetSelectedPOs();
      this.$refs.table.refresh();
    },
    clearFilter() {
      this.filters = {
        supplier: "",
        customer: "",
        status: "",
        from_date: "",
        to_date: "",
        po_number: ""
      };

      this.$nextTick(() => {
        this.filtersApplied = false;
      });
    },
    toggleFilter() {
      if (this.showFilters && this.filtersApplied) {
        this.clearFilter();
        this.refreshTable();
      }

      this.showFilters = !this.showFilters;
    },
    onSelectCustomer(customer) {
      this.filters.customer = customer;
    },
    async removepo(id) {
      this.id = id;
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("pos.confirm_delete", 1),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deletePO(this.id);
          if (res.data.success) {
            this.$refs.table.refresh();
            this.filtersApplied = false;
            this.resetSelectedPOs();
            window.toastr["success"](this.$tc("pos.deleted_message", 1));
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async convertInToinvoice(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_conversion_invoice"),
        icon: "/assets/icon/file-alt-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willConvertInToinvoice => {
        if (willConvertInToinvoice) {
          let res = await this.convertToInvoice(id);
          if (res.data && res.data.result === true) {
            window.toastr["success"](this.$t("pos.conversion_message"));
            this.$router.push(`invoices/${res.data.invoice.id}/edit`);
          }
          if (
            res.data &&
            res.data.result === false &&
            res.data.error === "already_invoice_generate"
          ) {
            window.toastr["error"](this.$t("pos.already_conversion_message"));
            this.$router.push(`invoices/${res.data.invoice.id}/edit`);
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async convertInToDeliveryNote(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_conversion_deliveryNote"),
        icon: "/assets/icon/file-alt-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willConvertInToDeliveryNote => {
        if (willConvertInToDeliveryNote) {
          let data = { purchase_order_id: id };
          let res = await this.convertToDeliveryNote(data);
          if (res.data && res.data.result === true) {
            window.toastr["success"](
              this.$t("deliveryNotes.conversion_message")
            );
            if (
              res.data.deliveryNote.purchase_order.items &&
              res.data.deliveryNote.purchase_order.items.length > 0
            ) {
              this.$router.push(
                `delivery-notes/${res.data.deliveryNote.id}/edit`
              );
            } else {
              this.$router.push(
                `manually-delivery-notes/${res.data.deliveryNote.id}/edit`
              );
            }
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async sendPOForFinancing(id) {
      swal({
        title: this.$t("pos.apply_for_po_financing"),
        text: this.$t("pos.confirmation_for_apply_for_po_financing"),
        icon: "/assets/icon/check-circle-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async value => {
        if (value) {
          this.poFinancingRequest(id);
        }
      });
    },
    async poFinancingRequest(id) {
      this.$router.push(`/supplier/purchase-orders/financing/${id}/request`);
    },
    async removeMultiplepo() {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$tc("pos.confirm_delete", 2),
        icon: "/assets/icon/trash-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willDelete => {
        if (willDelete) {
          let res = await this.deleteMultiplePOs();
          if (res.data.success) {
            this.$refs.table.refresh();
            this.resetSelectedPOs();
            this.filtersApplied = false;
            window.toastr["success"](this.$tc("pos.deleted_message", 2));
          } else if (res.data.error) {
            window.toastr["error"](res.data.message);
          }
        }
      });
    },
    async clearCustomerSearch(removedOption, id) {
      this.filters.customer = "";
      this.refreshTable();
    },
    async clearStatusSearch(removedOption, id) {
      this.filters.status = { name: "", value: "" };
      this.refreshTable();
    },
    async onMarkAsSent(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_mark_as_sent"),
        icon: "/assets/icon/check-circle-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willMarkAsSent => {
        if (willMarkAsSent) {
          const data = {
            purchase_order_id: id
          };
          let response = await this.markAsSent(data);
          this.refreshTable();
          if (response.data) {
            window.toastr["success"](this.$tc("pos.mark_as_sent_successfully"));
          }
        }
      });
    },
    async sendpo(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_send_po_electronically"),
        icon: "/assets/icon/paper-plane-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willSendpo => {
        if (willSendpo) {
          const data = {
            purchase_order_id: id
          };
          let response = await this.sendEmail(data);
          this.refreshTable();
          if (response.data.success) {
            window.toastr["success"](this.$tc("pos.send_po_successfully"));
            return true;
          }
          if (response.data.error === "user_email_does_not_exist") {
            window.toastr["error"](this.$tc("pos.user_email_does_not_exist"));
            return true;
          }
          window.toastr["error"](this.$tc("pos.something_went_wrong"));
        }
      });
    },
    async closePO(id) {
      swal({
        title: this.$t("general.are_you_sure"),
        text: this.$t("pos.confirm_close_po"),
        icon: "/assets/icon/cross-solid.svg",
        buttons: true,
        dangerMode: true
      }).then(async willClosedpo => {
        if (willClosedpo) {
          const data = {
            purchase_order_id: id
          };
          let response = await this.markAsClose(data);
          this.refreshTable();
          if (response.data.success) {
            window.toastr["success"](
              this.$tc("pos.mark_as_closed_successfully")
            );
            return true;
          }
          window.toastr["error"](this.$tc("pos.something_went_wrong"));
        }
      });
    },
    completionStatus(id) {
      if (this.userType === "super_admin") {
        this.$router.push(`/super-admin/purchase-orders/${id}/item-status`);
      } else if (this.userType === "anchor") {
        this.$router.push(`/anchor/purchase-orders/${id}/item-status`);
      } else if (this.userType === "supplier") {
        this.$router.push(`/supplier/purchase-orders/${id}/item-status`);
      }
    },
    async ImportItemFile() {
      this.openModal({
        title: "Import PO File",
        componentName: "ImportPOFile"
      });
    }
  }
};
</script>
